import { useTheme } from '@mui/material';
import { useWorkspaceContext } from 'contexts/workspace-context';
import { updateLedgerEntries } from 'graphql/ledger/mutations';
import {
  createOrderProperty,
  removeOrderProperty,
  updateOrderProperty,
} from 'graphql/properties/mutations';
import { getOrderProperties, GQLGetOrderProperties } from 'graphql/properties/queries';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { GraphqlError, OrderProperty } from 'types/order';
import useAlert from 'utils/alert';
import { LEDGER_CODE_LOAN_AMOUNT, PROPERTY_TAB } from 'utils/constants';
import { asyncForEach, capitalize } from 'utils/helpers';

import { extractPropertyAddress } from './utils/extractPropertyAddress';
import { extractPropertyData } from './utils/extractPropertyData';
import { initialValues } from './utils/initialValues';
import { useAccountingUpdateSocketEvent } from "hooks/useAccountingUpdateFromSocket";
import { useDeletePayoff } from 'hooks/ledger/mutations';
import { useDeleteLoan } from 'hooks/loans/mutations';
import { useDeleteProration } from 'hooks/prorations/mutations';
import { useInfoUpdateEvent } from 'hooks/useInfoUpdateEvent';

export const useOrderFlowProperties = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const theme = useTheme();
  const showAlert = useAlert();
  const queryCache = useQueryClient();
  const [selectedProperty, setSelectedProperty] = useState<OrderProperty>(initialValues);
  const [currentTab, setCurrentTab] = useState<string>(PROPERTY_TAB);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const { updateOrderTabPropertyAddress } = useWorkspaceContext();
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string>('');
  const [deletePropertyInformationLoading, setDeletePropertyInformationLoading] =
		useState<boolean>(false);

  const {
    handleSubmit,
    formState: {
      errors,
      isValid: dataIsValid,
      isDirty: dataHasChanges,
      dirtyFields: dataDirtyFields,
    },
    reset,
    control,
    getValues,
    setValue,
    watch,
  } = useForm<Omit<OrderProperty, 'loans'>>({ defaultValues: initialValues, mode: 'onChange' });
  const [errorMessage, setErrorMessage] = useState<string>('');

  useAccountingUpdateSocketEvent({
    callback: () => queryCache.invalidateQueries(['order-properties', orderId])
  });

  useInfoUpdateEvent(() => {
    queryCache.invalidateQueries(['order-properties', orderId]);
  }, (data) => ["createNewProperty", "deleteProperty", "updateProperty"].includes(data.kind));

  const { data, isLoading: isPropertiesDataLoading } = useQuery<GQLGetOrderProperties>(
    ['order-properties', orderId],
    () => getOrderProperties({ id: orderId }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (localdata) => {
        let { estates } = localdata.getOrder;
        if (estates && estates?.length > 0) {
          estates = estates.map((estate) => {
            const accounting = estate.accounting || [];
            return {
              ...estate,
              accounting: accounting.filter(
                (entry) => !entry.code.includes(LEDGER_CODE_LOAN_AMOUNT)
              ),
            };
          });

          updateOrderTabPropertyAddress(orderId, estates[0].address.street_address);

          if (selectedProperty._id === '') {
            setSelectedProperty(estates[0]);
            reset(estates[0]);
          } else {
            const property = estates.find(
              (orderProperty) => orderProperty._id === selectedProperty._id
            );
            setSelectedProperty(property ?? initialValues);
            reset(property ?? initialValues);
          }
        } else {
          if (!dataHasChanges) reset(initialValues);
          updateOrderTabPropertyAddress(orderId, '');
        }
      },
    }
  );

  useEffect(() => {
    if (orderId) {
      setSelectedProperty(initialValues);
    }
  }, [orderId]);

  const properties = data?.getOrder.estates || [];

  const {
    mutate: createProperty,
    isLoading: isCreatePropertyLoading,
    isError: isCreatePropertyError,
    reset: resetCreateError,
  } = useMutation(createOrderProperty, {
    onSuccess: async (createResponse) => {
      showAlert(t('properties:created'), 'success');

      queryCache.invalidateQueries(['party-order', orderId]);
      queryCache.invalidateQueries(['order-properties-premium', orderId]);
      queryCache.invalidateQueries([{ orderId }, 'prorations']);

      if (createResponse.createOrderProperty.estates) {
        const propertiesFromResponse = createResponse.createOrderProperty.estates;
        const propertyFormData = getValues();
        const propertyCreated = propertiesFromResponse.find(
          (property) =>
            property.address.street_address === propertyFormData.address.street_address &&
						property.address.postal_code === propertyFormData.address.postal_code &&
						property.address.locality === propertyFormData.address.locality &&
						property.address.state === propertyFormData.address.state
        );

        const newGetOrder = {
          ...data?.getOrder,
        };

        if (propertyCreated) {
          propertyCreated.accounting = [];
          newGetOrder.estates = [...(newGetOrder.estates || []), propertyCreated];
          setSelectedProperty(propertyCreated);
          reset(propertyCreated);
        }
        queryCache.setQueryData(['order-properties', orderId], { getOrder: newGetOrder });
      }
    },
  });

  const {
    mutate: updateProperty,
    isLoading: isUpdatePropertyLoading,
    isError: isUpdatePropertyError,
    reset: resetUpdateError,
  } = useMutation(updateOrderProperty, {
    onSuccess: () => {
      showAlert(t('properties:updated'), 'success');
      queryCache.invalidateQueries(['order-properties', orderId]);
      queryCache.invalidateQueries(['order-properties-premium', orderId]);
    },
  });

  useEffect(() => {
    const subscription = watch(() => {
      resetCreateError();
      resetUpdateError();
    });
    return () => subscription.unsubscribe();
  }, [watch, resetCreateError, resetUpdateError]);

  const {
    mutate: removeProperty,
    isLoading: isRemovePropertyLoading,
    isError: isRemovePropertyError,
    error: removePropertyError,
  } = useMutation(removeOrderProperty, {
    onSuccess: () => {
      setIsDeleteModalOpen(false);

      showAlert(t('properties:removed'), 'success');
      queryCache.invalidateQueries(['order-properties', orderId]);
      queryCache.invalidateQueries(['party-order', orderId]);
      queryCache.invalidateQueries(['order-properties-premium', orderId]);
      reset(initialValues);

      setSelectedProperty(initialValues);
      setDeleteErrorMessage('');
    },
    onError: (error: GraphqlError) => {
      if (error && error.response.errors) {
        setDeleteErrorMessage(error.response.errors[0].message);
        setIsDeleteModalOpen(false);
      }
    },
  });

  const { mutate: updateAccountingEntries, isLoading: isLoadingPurchasePrice } = useMutation(
    updateLedgerEntries,
    {
      onSuccess: () => {
        showAlert(t('properties:accounting-entries-updated'), 'success');
        queryCache.invalidateQueries(['order-properties', orderId]);
        queryCache.invalidateQueries(['order-properties-premium', orderId]);
      },
    }
  );

  const selectProperty = (id: string) => {
    const getOrderCache: GQLGetOrderProperties | undefined = queryCache.getQueryData([
      'order-properties',
      orderId,
    ]);
    const propertyList = getOrderCache?.getOrder.estates || [];
    if (propertyList && id !== '') {
      const property = propertyList.find((orderProperty) => orderProperty._id === id)!;
      if (property.accounting) {
        property.accounting = property?.accounting.filter(
          (entry) => !entry.code.includes(LEDGER_CODE_LOAN_AMOUNT)
        );
      }
      setSelectedProperty(property ?? initialValues);
      reset(property ?? initialValues);
    }
  };

  const onSaveProperty = handleSubmit(async (propertyInfo) => {
    if (!dataHasChanges) return;
    const { _id: propertyId, accounting } = propertyInfo;
    const propertyData = extractPropertyData(propertyInfo);

    if (propertyId.length > 0) {
      if (Object.keys(dataDirtyFields).some((key) => propertyData[key])) {
        updateProperty({
          id: orderId,
          propertyId,
          data: propertyData,
        });
      }

      if (dataDirtyFields.accounting) {
        updateAccountingEntries({
          id: orderId,
          entries: accounting.map((entry) => ({
            accountingId: entry?._id || '',
            data: {
              amount_override: entry.amount,
              letter: entry.letter,
              entry_date: entry.entry_date,
            },
          })),
        });
      }
    } else {
      const address = extractPropertyAddress(propertyInfo);
      createProperty({
        id: orderId,
        data: propertyData,
        address,
      });
    }
  });

  const onAddProperty = () => {
    reset(initialValues);
    setSelectedProperty(initialValues);
  };

  const onRemoveProperty = useCallback(async () => {
    removeProperty({
      id: orderId,
      propertyId: selectedProperty._id,
    });
  }, [removeProperty, orderId, selectedProperty._id]);

  const { mutate: deleteProration } = useDeleteProration({
    queryConfig: {
      onError: () => {
        showAlert(t('properties:error-delete-proration'), 'error');
      },
    },
  });

  const { mutate: deleteMortgageMutation } = useDeletePayoff({
    queryConfig: {
      onError: () => {
        showAlert(t('properties:error-delete-mortgage'), 'error');
      },
    },
  });
  const { mutate: deleteLoan } = useDeleteLoan({
    queryConfig: {
      onError: () => {
        showAlert(t('properties:error-delete-loan'), 'error');
      },
    },
  });

  const onConfirmRemovePropertyInformation = useCallback(async () => {
    setDeletePropertyInformationLoading(true);
    if (selectedProperty.loans) {
      await asyncForEach(
        selectedProperty.loans,
        (loan) =>
          deleteLoan({
            loanId: loan._id,
            orderId,
          }),
        3000
      );
    }

    if (
      data?.getOrder.mortgages?.some((mortagage) => mortagage.estate._id === selectedProperty._id)
    ) {
      const mortgages = data?.getOrder.mortgages?.filter(
        (mortagage) => mortagage.estate._id === selectedProperty._id
      );
      await asyncForEach(
        mortgages,
        (mortgage) =>
          deleteMortgageMutation({
            id: orderId,
            mortgageId: mortgage._id,
          }),
        3000
      );
    }

    const defaultProrations = ['Assessments', 'City/Town Taxes', 'County Taxes'];
    const propertyProrations =
			selectedProperty.prorations?.filter(
			  (proration) => !defaultProrations.includes(proration.description)
			) ?? [];
    if (propertyProrations.length > 0) {
      await asyncForEach(
        propertyProrations,
        (proration) =>
          deleteProration({
            orderId,
            prorationId: proration._id,
          }),
        3000
      );
    }

    await onRemoveProperty();
    setDeletePropertyInformationLoading(false);
  }, [data, orderId, selectedProperty, onRemoveProperty, deleteLoan, deleteMortgageMutation, deleteProration]);

  return {
    t,
    theme,
    properties,
    isPropertiesDataLoading,
    selectedProperty,
    selectProperty,
    currentTab,
    onSelectTab: setCurrentTab,
    onSaveProperty,
    errors,
    dataIsValid,
    control,
    isCreatePropertyLoading,
    isCreatePropertyError,
    isUpdatePropertyLoading,
    isUpdatePropertyError,
    onAddProperty,
    onRemoveProperty,
    isRemovePropertyLoading,
    isRemovePropertyError,
    removePropertyError,
    getValues,
    isSaveLoading: isCreatePropertyLoading || isUpdatePropertyLoading || isLoadingPurchasePrice,
    setValue,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    onConfirmRemovePropertyInformation,
    deleteErrorMessage,
    setDeleteErrorMessage,
    deletePropertyInformationLoading,
    errorMessage
  };
};
